import React, { useState } from 'react';
import { createRepository } from 'service/ncoreBoardService';
import { useNavigate } from 'react-router-dom';
import styles from './CreateRepoModal.module.scss';

const TEMPLATES = [
    { value: '', label: '— Нет —' },
    { value: 'readme', label: 'README.md' },
    { value: 'gitignore', label: '.gitignore' },
    { value: 'license', label: 'LICENSE (MIT)' },
];

export default function CreateRepoModal({ onClose }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: '',
        description: '',
        visibility: 'public',
        default_branch: 'main',
        topics: '',
        website: '',
    });
    const [loading, setLoading] = useState(false);
    const [nameError, setNameError] = useState('');

    function validateName(val) {
        if (!val) return 'Имя обязательно';
        if (!/^[a-zA-Z0-9_.-]+$/.test(val)) return 'Только латинские буквы, цифры, . _ -';
        if (val.length > 100) return 'Не более 100 символов';
        return '';
    }

    function handleNameChange(e) {
        const val = e.target.value;
        setForm(p => ({ ...p, name: val }));
        setNameError(validateName(val));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const err = validateName(form.name);
        if (err) { setNameError(err); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                topics: form.topics ? form.topics.split(',').map(t => t.trim()).filter(Boolean) : [],
            };
            const repo = await createRepository(payload);
            onClose();
            navigate(`/ncoreboard/${repo.owner?.username || repo.owner}/${repo.slug}`);
        } catch (err) {
            alert(err.message || 'Ошибка при создании репозитория');
        }
        setLoading(false);
    }

    return (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9D3EF7' }}>
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                        </svg>
                        Новый репозиторий
                    </h2>
                    <button className={styles.closeBtn} onClick={onClose}>✕</button>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    {/* Name */}
                    <div className={styles.field}>
                        <label className={styles.label}>Название репозитория <span className={styles.required}>*</span></label>
                        <input
                            className={`${styles.input} ${nameError ? styles.inputError : ''}`}
                            placeholder="my-project"
                            value={form.name}
                            onChange={handleNameChange}
                            required
                            autoFocus
                        />
                        {nameError && <span className={styles.error}>{nameError}</span>}
                        <span className={styles.hint}>Доступны латинские буквы, цифры, точки, подчёркивания и дефисы</span>
                    </div>

                    {/* Description */}
                    <div className={styles.field}>
                        <label className={styles.label}>Описание <span className={styles.optional}>(необязательно)</span></label>
                        <input
                            className={styles.input}
                            placeholder="Краткое описание проекта"
                            value={form.description}
                            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                        />
                    </div>

                    {/* Visibility */}
                    <div className={styles.field}>
                        <label className={styles.label}>Видимость</label>
                        <div className={styles.visibilityRow}>
                            <label className={`${styles.visOption} ${form.visibility === 'public' ? styles.visActive : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={form.visibility === 'public'}
                                    onChange={() => setForm(p => ({ ...p, visibility: 'public' }))}
                                />
                                <div className={styles.visInfo}>
                                    <span className={styles.visIcon}>🌍</span>
                                    <div>
                                        <span className={styles.visLabel}>Публичный</span>
                                        <span className={styles.visDesc}>Виден всем, в том числе без авторизации</span>
                                    </div>
                                </div>
                            </label>
                            <label className={`${styles.visOption} ${form.visibility === 'private' ? styles.visActive : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={form.visibility === 'private'}
                                    onChange={() => setForm(p => ({ ...p, visibility: 'private' }))}
                                />
                                <div className={styles.visInfo}>
                                    <span className={styles.visIcon}>🔒</span>
                                    <div>
                                        <span className={styles.visLabel}>Приватный</span>
                                        <span className={styles.visDesc}>Виден только вам и коллаборантам</span>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Default branch */}
                    <div className={styles.field}>
                        <label className={styles.label}>Ветка по умолчанию</label>
                        <input
                            className={styles.input}
                            value={form.default_branch}
                            onChange={e => setForm(p => ({ ...p, default_branch: e.target.value }))}
                            placeholder="main"
                        />
                    </div>

                    {/* Topics */}
                    <div className={styles.field}>
                        <label className={styles.label}>Темы <span className={styles.optional}>(через запятую)</span></label>
                        <input
                            className={styles.input}
                            placeholder="python, django, api"
                            value={form.topics}
                            onChange={e => setForm(p => ({ ...p, topics: e.target.value }))}
                        />
                    </div>

                    {/* Website */}
                    <div className={styles.field}>
                        <label className={styles.label}>Сайт <span className={styles.optional}>(необязательно)</span></label>
                        <input
                            className={styles.input}
                            placeholder="https://example.com"
                            type="url"
                            value={form.website}
                            onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                        />
                    </div>

                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
                            Отмена
                        </button>
                        <button type="submit" className={styles.createBtn} disabled={loading || !!nameError}>
                            {loading ? 'Создание...' : 'Создать репозиторий'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
